import * as React from 'react';
import { MapPin, Building2, Map, Search, X, Loader2 } from 'lucide-react';
import { Input } from 'components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from 'components/ui/popover';
import { useDebounce } from 'lib/use-debounce';
import { RouteGeoSuggestion, RouteLevel } from '../api';

const LEVEL_META: Record<RouteLevel, { label: string; Icon: React.ComponentType<{ className?: string }> }> = {
  city: { label: 'City', Icon: MapPin },
  district: { label: 'District', Icon: Building2 },
  location: { label: 'Location', Icon: MapPin },
  state: { label: 'State', Icon: Map },
};

export interface RouteSearchComboboxProps {
  value: RouteGeoSuggestion | null;
  onChange: (value: RouteGeoSuggestion | null) => void;
  onSearch: (q: string) => Promise<RouteGeoSuggestion[]>;
  placeholder?: string;
  disabled?: boolean;
}

export function RouteSearchCombobox({
  value,
  onChange,
  onSearch,
  placeholder = 'Search city, district, location or state',
  disabled = false,
}: RouteSearchComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState(value?.label ?? '');
  const [results, setResults] = React.useState<RouteGeoSuggestion[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const anchorRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Track the latest request so out-of-order responses don't clobber newer ones.
  const requestIdRef = React.useRef(0);

  const debounced = useDebounce(query, 300);

  React.useEffect(() => {
    const q = debounced.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const reqId = ++requestIdRef.current;
    setLoading(true);
    onSearch(q)
      .then(res => {
        if (requestIdRef.current === reqId) {
          setResults(res);
          setActiveIndex(0);
        }
      })
      .catch(() => {
        if (requestIdRef.current === reqId) setResults([]);
      })
      .finally(() => {
        if (requestIdRef.current === reqId) setLoading(false);
      });
  }, [debounced, onSearch]);

  const handleSelect = (s: RouteGeoSuggestion) => {
    onChange(s);
    setQuery(s.label);
    setResults([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setResults([]);
    setOpen(false);
    requestIdRef.current++; // cancel any in-flight result
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (value) onChange(null); // editing invalidates the committed selection
    setOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && results[activeIndex]) {
        e.preventDefault();
        handleSelect(results[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const q = debounced.trim();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            value={query}
            onChange={handleInputChange}
            onFocus={() => {
              if (query.trim().length >= 2 || results.length) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="pl-9 pr-9"
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
          />
          {query && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
        onInteractOutside={e => {
          if (anchorRef.current && anchorRef.current.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
        className="w-[var(--radix-popover-trigger-width)] p-0 max-h-[320px] overflow-y-auto"
      >
        {loading && (
          <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Searching…
          </div>
        )}
        {/* {!loading && q.length < 2 && (
          <div className="px-3 py-4 text-sm text-muted-foreground">
            Start typing to search.
          </div>
        )} */}
        {!loading && q.length >= 2 && results.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">No matches found.</div>
        )}
        {!loading &&
          results.map((s, idx) => {
            const { Icon, label } = LEVEL_META[s.level];
            return (
              <button
                key={`${s.level}:${s.value}:${idx}`}
                type="button"
                // keep focus in the input so typing can continue
                onMouseDown={e => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => handleSelect(s)}
                className={`flex w-full items-start gap-2.5 px-3 py-2 text-left ${
                  idx === activeIndex ? 'bg-accent text-accent-foreground' : ''
                }`}
              >
                <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="mx-1.5 text-muted-foreground">•</span>
                    <span className="font-medium text-foreground">{s.label}</span>
                  </span>
                  {s.subtitle && (
                    <span className="text-xs text-muted-foreground truncate">{s.subtitle}</span>
                  )}
                </div>
              </button>
            );
          })}
      </PopoverContent>
    </Popover>
  );
}

export default RouteSearchCombobox;
